import { createMachine, assign } from "xstate";
import { codesNoSDK, DEVICE_DESKTOP, DEVICE_MOBILE } from "../shared/Constants/Constants"
import { conditions } from '../shared/Utilities/biometricsConditions';
import { actions, machineActions } from '../shared/Utilities/biometricsActions';
import {
  decryptClientData,
  generateBfKey,
  refreshBfKey,
  decryptPrevalidateData
} from '../shared/Utilities/cryptoUtils';
import {
  getPublicKey,
  validateClientState,
  userActivation,
  transactionValidation,
  saveUserTries,
  prevalidateClient
} from '../Services/httpServices';
import {
  fetchValidateParameters,
  checkTermsAndConditions,
  checkCameraPermissions,
  checkUserState,
  checkCustomerTransaction,
  validateCookie,
  generateUrlQr
} from '../shared/Utilities/auxiliaryUtils';

const initialContext = {
    clientId: "",
    data: "",
    documentType: "TIPDOC_FS001",
    documentNumber: "1111111111",
    email: "",
    phone: "",
    phoneIndicative: "",
    transactionId: "",
    token: "",
    redirectUrl: "",
    recaptcha: "",
    processId: "",
    code: "",
    codeLog: "",
    enrollment: false,
    documentTypeEq: "",
    sessionToken: "",
    sessionId: "",
    customerTransaction: "",
    customerTransactionUrl: "",
    showTermsAndConditions: "",
    blocked: false,
    attemps: 0,
    encryptedData: "",
    encryptedDataClient: "",
    iv: "",
    ivClient: "",
    contextScreen: true,
    processFailed: false,
    bffKey: "",
    jweKey: "",
    aesKey: "",
    jws: "",
    cookie: "",
    dataStr:"",
    resultLog: "",
    challenge: false,
    dataResponse: "",
    isRedirect: false,
    cameraDenied: false,
    deviceType: false,
    channelId: "",
    channelSecret: "",
    inProcess: false,
    urlQR: "",
    isDesktopProcessInMobile: false, // Flag to indicate if the process is initiated from a desktop in a mobile context
    notifyDesktop: true, // Flag to indicate if the desktop notification should be sent (error events)
    isTestMode: false, // Flag to indicate if the machine is in test mode
    dataCookie : "", //JWE value, used to validate when is a refresh or a new transaction
    encryptedDataPrevalidate: "", //Data response of prevalidate service
    ivPrevalidate: "", //Iv of prevalidate service
  };

const biometricsMachine = createMachine(
    {
      id: "biometrics",
      initial: "validateCookie",
      context: initialContext,
      states: {
        validateCookie: {
          invoke: {
            id: "validateCookieId",
            src: validateCookie,
            onDone: [
              {
                cond:  conditions.isCookie,
                target: "validateParameters",
                actions: [actions.setSso, actions.setSetSession, actions.setSetDataCookie],
              },{
                target: "validateParameters",
              }
            ]
          },
        },
        validateParameters: {
          invoke: {
            id: "validateParametersId",
            src: fetchValidateParameters,
            onDone: [
              {
                cond:  conditions.isError,
                target: "errorData"
              },{
                target: "recaptcha",
                actions: assign({
                  isDesktopProcessInMobile: (_context, event) => event.data.isDesktopProcessInMobile,
                  clientId: (_context, event) => event.data.clientId,
                  data: (_context, event) => event.data.data,
                  sessionId: (_context, event) => event.data.sessionId,
                  cookie: (_context, event) => event.data.cookie,
                  customerTransactionUrl: (_context, event) => event.data.customerTransaction,
                  deviceType: (_context, event) => event.data.deviceType
                }),
              }
            ]
          },
        },
        recaptcha: {
          on: {
            SETRECAPTCHAVALUE: [
              {
                target: "getBfKey",
                actions: assign({
                  recaptcha: (_context, event) => event.recaptcha
                }),
              }
            ]
          }
        },
        getBfKey: {
          invoke: {
            id: "getBfKeyId",
            src: getPublicKey,
            onDone: [
              {
                cond: (_context, event) => event.data.key,
                target: "generateBfKey",
                actions: [assign({
                  bffKey: (_context, event) => event.data.key,
                  codeLog: _context => 'successGettingKey' 
                }), "saveLogs"],
              },{
                target: "errorData",
                actions: [assign({ codeLog: _context => 'errorGettingKey' }), "saveLogs"]
              }
            ],
            onError: {
              target: "errorData",
              actions: [assign({ codeLog: _context => 'errorGettingKey' }), "saveLogs"]
            },
          },
        },
        generateBfKey: {
          invoke: {
            id: "generateBfKeyId",
            src: generateBfKey,
            onDone: [
              {
                cond: conditions.successEncryptKey,
                target: "prevalidateClient",
                actions: [actions.setJweKey, actions.setAesKey, actions.setDataStr,actions.setSuccessGeneretingKey, "saveLogs"],
              },{
                target: "errorData",
                actions: [assign({ codeLog: _context => 'errorGeneretingKey' }), "saveLogs"]
              }
            ]
          },
        },
        refreshKeyValidateClientSoyYo: {
          invoke: {
            id: "refreshKeyValidateId",
            src: refreshBfKey,
            onDone: [
              {
                cond: conditions.successRefreshKey,
                target: "validateClientSoyYo",
                actions: [actions.setJweKey],
              },{
                target: "errorData",
                actions: [assign({
                  codeLog: _context => codesNoSDK.errorGeneretingKey.codeLog,
                })]
              }
            ]
          },
        },
        prevalidateClient: {
          invoke: {
            id: "prevalidateClientId",
            src: prevalidateClient,
            onDone: [
              {
                cond: (_context, event) => event.data.resultCode === codesNoSDK.successCode.code && event.data.data,
                target: "decryptDataPrevalidate",
                actions: assign({
                  encryptedDataPrevalidate: (_context, event) => event.data.data,
                  ivPrevalidate: (_context, event) => event.data.id,
                  traceId: (_context, event) => event.data.traceId,
                  redirectUrl: (_context, event) => event.data.urlRedirect
                  })
              },{
                cond: (_context, event) => event.data.resultCode && event.data.urlRedirect,
                target: "errorProcess",
                actions: [assign({
                  code: (_context, event) => event.data.resultCode,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  resultLog: (_context, event) => event.data.resultCode,
                  codeLog: _context => 'errorServiceConsume'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.unauthorized.code,
                target: "unauthorized",
                actions: [assign({ codeLog: _context => 'unauthorized' }), actions.clearResultCode, "saveLogs"]
              },
              {
                target: "errorData",
              }
            ],
            onError: {
              target: "errorData"
            }
          },
        },
        decryptDataPrevalidate: {
          invoke: {
            id: "decryptDataPrevalidateId",
            src: decryptPrevalidateData,
            onDone: [
              {
                cond: (_context, event) => event.error,
                target: "errorData"
              },
              {
                cond: (_context, event) => event.data?.applies === true && event.data?.url,
                target: "redirectNewFront",
                actions: [assign({
                  cookie: (_context, event) => event.data.sso ? event.data.sso : _context.cookie,
                  redirectUrl: (_context, event) => event.data.url,
                  codeLog: _context => 'successDecryptingData',
                  resultLog: (_context, event) => `applies=${event.data?.applies}`
                }), "saveCookiePrevalidate", "saveLogs"]
              },
              {
                cond: (_context, event) => event.data?.applies === false,
                target: "validateClientSoyYo",
                 actions: [assign({
                  cookie: (_context, event) => event.data.sso ? event.data.sso : _context.cookie,
                  codeLog: _context => 'successDecryptingData',
                  resultLog: (_context, event) => `applies=${event.data?.applies}`
                }), "saveCookiePrevalidate", "saveLogs"]
              },
              {
                target: "errorData"
              }
            ],
            onError: {
              target: "errorData"
            }
          }
        },
        validateClientSoyYo: {
          invoke: {
            id: "validateClientId",
            src: validateClientState,
            onDone: [
              {
                cond: (_context, event) => event.data.resultCode === codesNoSDK.successCode.code && event.data.data,
                target: "decryptData",
                actions: assign({
                  encryptedDataClient: (_context, event) => event.data.data,
                  encryptedData: (_context, event) => event.data.data,
                  ivClient: (_context, event) => event.data.id,
                  iv: (_context, event) => event.data.id,
                  traceId: (_context, event) => event.data.traceId,
                })
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.failedRecatpchaRetry.code && !_context.challenge,
                target: "challenge",
                actions: [assign({
                  challenge: _context => true,
                  resultLog: (_context, event) => event.data.resultDescription,
                  codeLog: _context => 'failedRecatpchaRetry'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.failedRecatpcha.code,
                target: "errorProcess",
                actions: [assign({
                  code: _context => codesNoSDK.unauthorized.code,
                  resultLog: (_context, event) => event.data.resultDescription,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  codeLog: _context => 'failedRecatpcha'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.failedRecatpchaChallenge.code,
                target: "errorProcess",
                actions: [assign({
                  code: _context => codesNoSDK.unauthorized.code,
                  resultLog: (_context, event) => event.data.resultDescription,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  codeLog: _context => 'failedRecatpchaChallenge'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.urlRedirect,
                target: "errorProcess",
                actions: [assign({
                  code: (_context, event) => event.data.resultCode,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  resultLog: (_context, event) => event.data.resultCode,
                  codeLog: _context => 'errorServiceConsume'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.unauthorized.code,
                target: "unauthorized",
                actions: [assign({ codeLog: _context => 'unauthorized' }), actions.clearResultCode, "saveLogs"]
              },
              {
                target: "errorData",
                actions: [assign({ codeLog: _context => 'errorServiceConsume' }), actions.clearResultCode, "saveLogs"]
              }
            ],
            onError: [
              {
                target: "errorData",
                actions: [assign({ codeLog: _context => 'errorServiceConsume' }), actions.clearResultCode, "saveLogs"]
              }
            ],
          }
        },
        decryptData: {
          invoke: {
            id: "decryptDataId",
            src: decryptClientData,
            onDone: [
              {
                cond: (_context, event) => (event.data.resultCode === "F071" || event.data.resultCode === "F070") && _context.deviceType === DEVICE_MOBILE,
                target: "checkCustomerTransaction",
                actions: [assign({
                  enrollment: (_context, event) => event.data.resultCode ===  "F071",
                  transactionId: (_context, event) => event.data.data.transactionId,
                  token: (_context, event) => event.data.token,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  documentNumber: (_context, event) => event.data.data.customer.documentNumber,
                  documentType: (_context, event) => event.data.data.customer.documentType,
                  email: (_context, event) => event.data.data.customer.email,
                  phone: (_context, event) => event.data.data.customer.phone,
                  phoneIndicative: (_context, event) => event.data.data.customer.phoneIndicative,
                  customerTransaction: (_context, event) => event.data.data.customer.customerTransaction,
                  showTermsAndConditions: (_context, event) => event.data.resultCode ===  "F071" ? false :  event.data.data.termsConditions,
                  blocked: (_context, event) => event.data.data.block,
                  attemps: (_context, event) => event.data.data.blockTry ? event.data.data.blockTry : 1,
                  cookie: (_context, event) => event.data.sso ?  event.data.sso  : "",
                  codeLog: _context => 'successDecryptingData',
                  resultLog: (_context, event) => `resultCode=${event.data.resultCode}&resultDescription=${event.data.resultDescription}`
                }), "documentEquivalent", "saveCookiesInBrowser", "saveLogs"]
              },
              {
                cond: (_context, event) => (event.data.resultCode === "F071" || event.data.resultCode === "F070") && _context.deviceType === DEVICE_DESKTOP,
                target: "checkCustomerTransactionQr",
                actions: [assign({
                  inProcess: (_context, event) => event.data.inProcess,
                  channelId: (_context, event) => event.data.channelId,
                  channelSecret: (_context, event) => event.data.channelSecret,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  documentNumber: (_context, event) => event.data.data.customer.documentNumber,
                  documentType: (_context, event) => event.data.data.customer.documentType,
                  email: (_context, event) => event.data.data.customer.email,
                  phone: (_context, event) => event.data.data.customer.phone,
                  phoneIndicative: (_context, event) => event.data.data.customer.phoneIndicative,
                  customerTransaction: (_context, event) => event.data.data.customer.customerTransaction,
                  showTermsAndConditions: (_context, event) => event.data.resultCode ===  "F071" ? false :  event.data.data.termsConditions,
                  blocked: (_context, event) => event.data.data.block,
                  attemps: (_context, event) => event.data.data.blockTry ? event.data.data.blockTry : 1,
                  cookie: (_context, event) => event.data.sso ?  event.data.sso  : "",
                  codeLog: _context => 'successDecryptingData',
                  resultLog: (_context, event) => `resultCode=${event.data.resultCode}&resultDescription=${event.data.resultDescription}`
                }), "documentEquivalent", "saveCookiesInBrowser", "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.failedRecatpchaRetry.code && !_context.challenge,
                target: "challenge",
                actions: [assign({
                  challenge: _context => true,
                  resultLog: (_context, event) => event.data.resultDescription,
                  codeLog: _context => 'failedRecatpchaRetry'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.urlRedirect,
                target: "errorProcess",
                actions: [assign({
                  code: (_context, event) => event.data.resultCode,
                  redirectUrl: (_context, event) => event.data.urlRedirect,
                  resultLog: (_context, event) => event.data.resultCode,
                  codeLog: _context => 'successDecryptingData'
                }), "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode && event.data.resultCode === codesNoSDK.unauthorized.code,
                target: "unauthorized",
                actions: [assign({ codeLog: _context => 'unauthorized' }), actions.clearResultCode, "saveLogs"]
              },
              {
                cond: (_context, event) => event.data.resultCode,
                target: "errorData",
                actions: [assign({ 
                  codeLog: _context => 'successDecryptingData',
                  resultLog: (_context, event) => event.data.resultCode,
                }), "saveLogs"]
              },
              {
                target: "errorData",
                actions: [assign({ 
                  codeLog: _context => 'successDecryptingData',
                  resultLog: (_context, event) => event.data,
                }), "saveLogs"]
              }
            ],
            onError: {
              target: "errorData",
              actions: [assign({ codeLog: _context => 'errorDecryptingData' }), actions.clearResultCode, "saveLogs"]
            },
          }
        },
        checkCustomerTransaction: {
          invoke: {
            id: "checkCustomerTransactionId",
            src: checkCustomerTransaction,
            onDone: [
              {
                cond: (_context, event) => event.data.mismatch,
                target: "errorProcess",
                actions: [assign({
                  code: _context => codesNoSDK.customerTransactionMismatch.code,
                  codeLog: _context => codesNoSDK.customerTransactionMismatch.codeLog,
                })]
              },
              {
                cond: _context => _context.deviceType === DEVICE_MOBILE && _context.isDesktopProcessInMobile,
                target: "contextQr",
                actions: [assign({
                  codeLog: _context => 'showContextQr'
                }), actions.clearResultCode, "saveLogs"]
              },
              {
                target: "checkUserState"
              }
            ]
          }
        },
        checkCustomerTransactionQr: {
          invoke: {
            id: "checkCustomerTransactionQrId",
            src: checkCustomerTransaction,
            onDone: [
              {
                cond: (_context, event) => event.data.mismatch,
                target: "errorProcess",
                actions: [assign({
                  codeLog: _context => codesNoSDK.customerTransactionMismatch.codeLog,
                  code: _context => codesNoSDK.customerTransactionMismatch.code
                })]
              },
              {
                target: "generateUrlQr",
                actions: [actions.clearResultCode]
              }
            ]
          }
        },
        checkUserState: {
          invoke: {
            id: "checkUserStateId",
            src: checkUserState,
            onDone: [
              {
                cond: (_context, event) => event.data.blocked,
                target: "errorProcess",
                actions: [assign({
                  code: _context => codesNoSDK.errorBlockedUser.code,
                  codeLog: _context => codesNoSDK.errorBlockedUser.codeLog,
                })]
              },
              {
                cond: _context =>_context.contextScreen,
                target: "contexFlow",
                actions: [assign({
                          contextScreen: _context => false,
                          codeLog: _context => 'showContext'
                        }), actions.clearResultCode, "saveLogs"]
              },
              {
                target: "checkTermsAndConditions"
              }
            ]
          }
        },
        generateUrlQr: {
          invoke: {
            id: "generateUrlQrId",
            src: generateUrlQr,
            onDone: [
              {
                cond: (_context, event) => conditions.successUrlQr(_context, event),
                target: "triggerAsyncClient",
                actions: [
                  actions.setUrlQR,
                  actions.setLogQr,
                  actions.clearResultCode,
                  "saveLogs"
                ]
              },
              {
                target: "errorData",
                actions: [assign({
                  codeLog: _context => codesNoSDK.errorGeneretingJweQr.codeLog
                })]
              }
            ]
          }
        },
        triggerAsyncClient: {
          after: {
            10: [
              {
                cond: _context => !_context.inProcess,
                target: "showQr"
              },
              {
                target: "showProgressMobile"
              }
            ]
          }
        },
        showQr: {
          on: {
            JWEUSED: [
              {
                target: "showProgressMobile",
                actions: [actions.setLogQrSuccess, "saveLogs"]
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        contextQr: {
          on: {
            FINISH: [
              {
                target: "success",
                actions: [actions.setDataResponse, actions.setCodeResult]
              },
            ],
            CONTINUE: [
              {
                target: "checkUserState",
                actions: [assign({
                  codeLog: _context => 'finishContextQr' 
                }), actions.clearResultCode, "saveLogs"]
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        showProgressMobile: {
          on: {
            FINISH: [
              {
                target: "success",
                actions: [actions.setDataResponse, actions.setCodeResult]
              },
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
            // This event is used to continue the process only executed in test mode
            CONTINUE: [
              {
                target: "validateClientSoyYo",
                actions: [actions.setDeviceType, actions.setIsDesktopProcessInMobile, actions.setIsTestMode]
              }
            ],
          }
        },
        challenge: {
          on: {
            SETRECAPTCHAVALUE: [
              {
                target: "refreshKeyValidateClientSoyYo",
                actions: assign({
                  recaptcha: (_context, event) => event.recaptcha
                }),
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        contexFlow: {
          on: {
            CONTINUE: [
              {
                target: "checkTermsAndConditions",
                actions: [assign({
                  codeLog: _context => 'finishContext' 
                }), actions.clearResultCode, "saveLogs"]
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        checkTermsAndConditions: {
          invoke: {
            id: "checkTermsAndConditionsId",
            src: checkTermsAndConditions,
            onDone: [
              {
                cond: (_context, event) => event.data.showTyC,
                target: "TermsAndConditions",
                actions: [assign({
                  codeLog: _context => 'showTaC' 
                }), actions.clearResultCode, "saveLogs"]
              },{
                target: "checkCameraPermissions"
              }
            ]
          }
        },
        TermsAndConditions: {
          on: {
            ACEPTTERMS: [
              {
                target: "checkCameraPermissions",
                actions: [assign({ codeLog: _context => 'successTAC' }), actions.clearResultCode, "saveLogs"]
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        checkCameraPermissions: {
          invoke: {
            id: "checkCameraPermissionssId",
            src: checkCameraPermissions,
            onDone: [
              {
                cond: (_context, event) => !event.data,
                target: "CameraScreen",
                actions: [assign({ codeLog: _context => 'initCameraScreen' }), actions.clearResultCode, "saveLogs"]
              },{
                cond: _context => _context.enrollment,
                target: "enrollmentUser"
              },
              {
                target: "authenticateUser"
              }
            ]
          }
        },
        CameraScreen: {
          on: {
            CAMERARESULT: [
              {
                cond: _context => _context.enrollment,
                target: "enrollmentUser",
                actions: [assign({ codeLog: _context => 'cameraTrue' }), actions.clearResultCode, "saveLogs"]
              },{
                target: "authenticateUser",
                actions: [assign({ codeLog: _context => 'cameraTrue' }), actions.clearResultCode, "saveLogs"]
              }
            ],
            CAMERAUNKNOWN: [
              {
                cond: _context => _context.enrollment,
                target: "enrollmentUser",
                actions: [assign({ 
                  codeLog: _context => 'cameraUnknown',
                  resultLog: (_context, event) => event.error
                 }), "saveLogs"]
              },{
                target: "authenticateUser",
                actions: [assign({ 
                  codeLog: _context => 'cameraUnknown',
                  resultLog: (_context, event) => event.error
                 }), "saveLogs"]
              }
            ],
            CAMERAERROR: [
              {
                cond: _context => !_context.cameraDenied,
                target: "cameraError",
                actions: [assign({ 
                  codeLog: _context => 'cameraFalse',
                  cameraDenied: _context => true
                 }), actions.clearResultCode, "saveLogs"]
              },
              {
                cond: _context => _context.enrollment,
                target: "errorProcess",
                actions: [assign({ 
                  codeLog: _context => 'failedCameraEnrollment',
                  code: _context => codesNoSDK.failedCameraEnrollment.code
                 }), actions.clearResultCode, "saveLogs"]
              },
              {
                target: "errorProcess",
                actions: [assign({ 
                  codeLog: _context => 'failedCameraAuthentication',
                  code: _context => codesNoSDK.failedCameraAuthentication.code
                 }), actions.clearResultCode, "saveLogs"]
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        authenticateUser: {
          on: {
            AUTHENTICATED: [
              {
                target: "authorizeTransaction",
                actions: assign({
                  sessionToken: (_context, event) => event.sessionToken
                }),
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
            SAVETRIES: [
              {
                target: "refreshKeyRetries",
              }
            ],
            SAVETRIESANDOUT: [
              {
                actions: assign({
                  processFailed: _context => true,
                  code: (_context, event) => event.code,
                }),
                target: "refreshKeyRetries",
              }
            ],
            CAMERAERROR: [
              {
                target: "cameraError",
              }
            ],
          }
        },
        cameraError: {
          on: {
            RETRY: [
              {
                target: "checkCameraPermissions"
              },
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
          }
        },
        enrollmentUser: {
          on: {
            ACTIVATEUSER: [
              {
                target: "refreshKeyActivateUser",
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ],
            SAVETRIES: [
              {
                target: "refreshKeyRetries",
              }
            ],
            CAMERAERROR: [
              {
                target: "cameraError",
              }
            ],
          }
        },
        refreshKeyRetries: {
          invoke: {
            id: "refreshKeyRetriesId",
            src: refreshBfKey,
            onDone: [
              {
                cond: conditions.successRefreshKey,
                target: "saveUserTries",
                actions: [actions.setJweKey],
              },{
                target: "errorData",
                actions: [assign({
                  codeLog: _context => codesNoSDK.errorGeneretingKey.codeLog,
                })]
              }
            ]
          },
        },
        saveUserTries: {
          invoke: {
            id: "saveUserTriesId",
            src: saveUserTries,
            onDone: [
              {
                cond: _context => _context.processFailed,
                target: "errorProcess"
              },
              {
                cond: (_context, event) => event.data.resultCode === codesNoSDK.successCode.code,
                target: "retry"
              },
              {
                cond: (_context, event) => event.data.resultCode,
                target: "errorProcess",
                actions: [actions.setCodeResult]
              }
            ],
            onError:[ 
              {
                cond:  conditions.successServiceConsume,
                actions: [actions.setServiceError],
                target: "errorProcess",
              },
              {
                actions: [actions.setDefaultServiceError],
                target: "errorProcess",
              }
            ]
          }
        },
        retry: {
          on: {
            RETRY: [
              {
                target: "checkUserState",
                actions: [assign({
                  showTermsAndConditions: _context => true,
                })]
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ]
          }
        },
        refreshKeyActivateUser: {
          invoke: {
            id: "refreshKeyActivateUserId",
            src: refreshBfKey,
            onDone: [
              {
                cond: conditions.successRefreshKey,
                target: "activateUser",
                actions: [actions.setJweKey],
              },{
                target: "errorData",
                actions: [assign({
                  codeLog: _context => codesNoSDK.errorGeneretingKey.codeLog,
                })]
              }
            ]
          },
        },
        activateUser: {
          invoke: {
            id: "activateUserId",
            src: userActivation,
            onDone: [
              {
                cond: (_context, event) => event.data.resultCode === codesNoSDK.successCode.code,
                actions: assign({
                  sessionToken: (_context, event) => event.data.data.sessionToken
                }),
                target: "authorizeTransaction",
              },
              {
                cond: (_context, event) => event.data.resultCode === codesNoSDK.enrollmentFailed.code,
                actions: assign({
                  processFailed: _context => true,
                  code: _context => 'BF006'
                }),
                target: "refreshKeyRetries",
              },
              {
                target: "errorProcess",
                actions: [actions.setCodeResult]
              }
            ],
            onError:[ 
              {
                cond:  conditions.successServiceConsume,
                actions: [actions.setServiceError, actions.setJwsValue],
                target: "errorProcess",
              },
              {
                actions: [actions.setDefaultServiceError],
                target: "errorProcess",
              }
            ]
          }
        },
        authorizeTransaction: {
          on: {
            AUTHORIZATIONTRANSACTION: [
              {
                target: "refreshKeyTransaction",
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ]
          },
        },
        refreshKeyTransaction: {
          invoke: {
            id: "refreshKeyTransactionId",
            src: refreshBfKey,
            onDone: [
              {
                cond: conditions.successRefreshKey,
                target: "validateTransaction",
                actions: [actions.setJweKey],
              },{
                target: "errorData",
                actions: [assign({
                  codeLog: _context => codesNoSDK.errorGeneretingKey.codeLog,
                })]
              }
            ]
          },
        },
        validateTransaction: {
          invoke: {
            id: "validateTransactionId",
            src: transactionValidation,
            onDone: [
              {
                cond: (_context, event) => event.data.resultCode === codesNoSDK.successCode.code && event.data.data,
                target: "success",
                actions: [actions.setDataResponse, actions.setCodeResult]
              },
              {
                target: "errorProcess",
                actions: [assign({
                  notifyDesktop: _context => false
                }), actions.setCodeResult]
              }
            ],
            onError:[ 
              {
                cond:  conditions.successServiceConsume,
                actions: [assign({
                  notifyDesktop: _context => false
                }), actions.setServiceError],
                target: "errorProcess",
              },
              {
                actions: [assign({
                  notifyDesktop: _context => false
                }), actions.setDefaultServiceError],
                target: "errorProcess",
              }
            ]
          }
        },
        cancelOperation: {
          on: {
            RESTART: [
              {
                target: "validateClientSoyYo",
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ]
          },
        },
        retries: {
          on: {
            RESTART: [
              {
                target: "validateClientSoyYo",
              }
            ],
            REDIRECTERROR: [
              {
                target: "errorProcess",
                actions: [actions.setCodeError]
              }
            ]
          },
        },
        success: {
          after: {
            1: {
              target: "prepareRedirectSuccess",
              actions: [assign({
                codeLog: _context => 'redirectResponse',
                resultLog: _context => _context.code,
                isRedirect: _context => true
              }),"deleteCookiesInBrowser"]
            },
          },
        },
        unauthorized: {
          type: "final",
        },
        errorData: {
          type: "final",
        },
        failed: {
          type: "final",
        },
        successProcess: {
          type: "final",
        },
        redirectWithError: {
          after: {
            1000:{
              actions: "redirectToTargetError"
            }
          }
        },
        redirectWithSuccess: {
          after: {
            1000:{
              actions: "redirectToTarget"
            }
          }
        },
        redirectNewFront: {
          after: {
            1000:{
              actions: "redirectToNewFront"
            }
          }
        },
        errorProcess: {
          after: {
            1: {
              target: "prepareRedirectError",
              actions: [assign({
                codeLog: _context => 'redirectResponse',
                resultLog: _context => _context.code,
                isRedirect: _context => true
              }), "deleteCookiesInBrowser"]
            },
          },
        },
        prepareRedirectError: {
          after: {
            1: [
              {
                cond: _context => !_context.isTestMode && _context.deviceType === DEVICE_MOBILE && _context.isDesktopProcessInMobile,
                target: "failed",
                actions: [assign({
                  codeLog: _context => 'showFinishError'
                }), actions.clearResultCode, "sendFrontMessage", "resetHistory"]
              },
              {
                target: "redirectWithError",
                actions: ["closeSession", "saveLogs"]
              }
            ],
          },
        },
        prepareRedirectSuccess: {
          after: {
            1: [
              {
                cond: _context => !_context.isTestMode && _context.deviceType === DEVICE_MOBILE && _context.isDesktopProcessInMobile,
                target: "successProcess",
                actions: [assign({
                  codeLog: _context => 'showFinishSuccess'
                }), actions.clearResultCode, "resetHistory"]
              },
              {
                target: "redirectWithSuccess",
                actions: "saveLogs"
              }
            ],
          },
        }
      }
    },
    {
      actions: {
        ...machineActions
      }
    }
);

export default biometricsMachine;